/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { CustomerDetail } from '../models/CustomerDetail';
import type { CustomerUpsert } from '../models/CustomerUpsert';
import type { GetToken } from '../models/GetToken';
import type { PaginatedCustomerListList } from '../models/PaginatedCustomerListList';
import type { PaginatedCustomerStatisticsList } from '../models/PaginatedCustomerStatisticsList';
import type { PaginatedUserDetailList } from '../models/PaginatedUserDetailList';
import type { PatchedUserProfile } from '../models/PatchedUserProfile';
import type { UserDetail } from '../models/UserDetail';
import type { UserProfile } from '../models/UserProfile';

import type { CancelablePromise } from '../core/CancelablePromise';
import { OpenAPI } from '../core/OpenAPI';
import { request as __request } from '../core/request';

export class UsersService {

  /**
   * @param clientType Filtrează după tipul clientului: potential, active_with_account, active_without_account.
 *
 * * `potential` - Potential clients - without completed orders and without accounts
 * * `active_with_account` - Active clients with accounts - have completed orders and user accounts
 * * `active_without_account` - Active clients without accounts - have completed orders but no user accounts
   * @param email 
   * @param excludeIds comma separated list
   * @param ids comma separated list
   * @param lastOrderDateGte Filtrează clienții cu data ultimei comenzi mai mare sau egală decât valoarea transmisă.
   * @param lastOrderDateLte Filtrează clienții cu data ultimei comenzi mai mică sau egală decât valoarea transmisă.
   * @param ordering Ordering fields: id, created, modified, email, phone, first_name, last_name, last_order_date, paid_amount
   * @param page A page number within the paginated result set.
   * @param pageSize Number of results to return per page.
   * @param paidAmountGte Filtrează clienții cu suma plătită mai mare sau egală decât valoarea transmisă.
   * @param paidAmountLte Filtrează clienții cu suma plătită mai mică sau egală decât valoarea transmisă.
   * @param product 
   * @param registered Filtrează clienții în funcție de existența unui cont de utilizator asociat.
   * @param search Search fields: email, first_name, last_name, phone
   * @returns PaginatedCustomerListList 
   * @throws ApiError
   */
  public static usersCustomersList(
clientType?: 'active_with_account' | 'active_without_account' | 'potential',
email?: string,
excludeIds?: string,
ids?: string,
lastOrderDateGte?: string,
lastOrderDateLte?: string,
ordering?: string,
page?: number,
pageSize?: number,
paidAmountGte?: number,
paidAmountLte?: number,
product?: Array<number>,
registered?: boolean,
search?: string,
): CancelablePromise<PaginatedCustomerListList> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/users/customers/',
      query: {
        'client_type': clientType,
        'email': email,
        'exclude_ids': excludeIds,
        'ids': ids,
        'last_order_date_gte': lastOrderDateGte,
        'last_order_date_lte': lastOrderDateLte,
        'ordering': ordering,
        'page': page,
        'page_size': pageSize,
        'paid_amount_gte': paidAmountGte,
        'paid_amount_lte': paidAmountLte,
        'product': product,
        'registered': registered,
        'search': search,
      },
    });
  }

  /**
   * @param id A unique integer value identifying this customer.
   * @returns CustomerDetail 
   * @throws ApiError
   */
  public static usersCustomersRetrieve(
id: number,
): CancelablePromise<CustomerDetail> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/users/customers/{id}/',
      path: {
        'id': id,
      },
    });
  }

  /**
   * @param clientType Filtrează după tipul clientului: potential, active_with_account, active_without_account.
 *
 * * `potential` - Potential clients - without completed orders and without accounts
 * * `active_with_account` - Active clients with accounts - have completed orders and user accounts
 * * `active_without_account` - Active clients without accounts - have completed orders but no user accounts
   * @param email 
   * @param excludeIds comma separated list
   * @param ids comma separated list
   * @param lastOrderDateGte Filtrează clienții cu data ultimei comenzi mai mare sau egală decât valoarea transmisă.
   * @param lastOrderDateLte Filtrează clienții cu data ultimei comenzi mai mică sau egală decât valoarea transmisă.
   * @param ordering Ordering fields: id, created, modified, email, phone, first_name, last_name, last_order_date, paid_amount
   * @param paidAmountGte Filtrează clienții cu suma plătită mai mare sau egală decât valoarea transmisă.
   * @param paidAmountLte Filtrează clienții cu suma plătită mai mică sau egală decât valoarea transmisă.
   * @param product 
   * @param registered Filtrează clienții în funcție de existența unui cont de utilizator asociat.
   * @param search Search fields: email, first_name, last_name, phone
   * @returns binary 
   * @throws ApiError
   */
  public static usersCustomersCsvRetrieve(
clientType?: 'active_with_account' | 'active_without_account' | 'potential',
email?: string,
excludeIds?: string,
ids?: string,
lastOrderDateGte?: string,
lastOrderDateLte?: string,
ordering?: string,
paidAmountGte?: number,
paidAmountLte?: number,
product?: Array<number>,
registered?: boolean,
search?: string,
): CancelablePromise<Blob> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/users/customers/csv/',
      query: {
        'client_type': clientType,
        'email': email,
        'exclude_ids': excludeIds,
        'ids': ids,
        'last_order_date_gte': lastOrderDateGte,
        'last_order_date_lte': lastOrderDateLte,
        'ordering': ordering,
        'paid_amount_gte': paidAmountGte,
        'paid_amount_lte': paidAmountLte,
        'product': product,
        'registered': registered,
        'search': search,
      },
    });
  }

  /**
   * @param requestBody 
   * @returns GetToken 
   * @throws ApiError
   */
  public static usersCustomersGuestTokenCreate(
requestBody: CustomerUpsert,
): CancelablePromise<GetToken> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/users/customers/guest-token/',
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @param clientType Filtrează după tipul clientului: potential, active_with_account, active_without_account.
 *
 * * `potential` - Potential clients - without completed orders and without accounts
 * * `active_with_account` - Active clients with accounts - have completed orders and user accounts
 * * `active_without_account` - Active clients without accounts - have completed orders but no user accounts
   * @param email 
   * @param excludeIds comma separated list
   * @param ids comma separated list
   * @param lastOrderDateGte Filtrează clienții cu data ultimei comenzi mai mare sau egală decât valoarea transmisă.
   * @param lastOrderDateLte Filtrează clienții cu data ultimei comenzi mai mică sau egală decât valoarea transmisă.
   * @param ordering Ordering fields: id, created, modified, email, phone, first_name, last_name, last_order_date, paid_amount
   * @param page A page number within the paginated result set.
   * @param pageSize Number of results to return per page.
   * @param paidAmountGte Filtrează clienții cu suma plătită mai mare sau egală decât valoarea transmisă.
   * @param paidAmountLte Filtrează clienții cu suma plătită mai mică sau egală decât valoarea transmisă.
   * @param product 
   * @param registered Filtrează clienții în funcție de existența unui cont de utilizator asociat.
   * @param search Search fields: email, first_name, last_name, phone
   * @returns PaginatedCustomerStatisticsList 
   * @throws ApiError
   */
  public static usersCustomersStatisticsList(
clientType?: 'active_with_account' | 'active_without_account' | 'potential',
email?: string,
excludeIds?: string,
ids?: string,
lastOrderDateGte?: string,
lastOrderDateLte?: string,
ordering?: string,
page?: number,
pageSize?: number,
paidAmountGte?: number,
paidAmountLte?: number,
product?: Array<number>,
registered?: boolean,
search?: string,
): CancelablePromise<PaginatedCustomerStatisticsList> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/users/customers/statistics/',
      query: {
        'client_type': clientType,
        'email': email,
        'exclude_ids': excludeIds,
        'ids': ids,
        'last_order_date_gte': lastOrderDateGte,
        'last_order_date_lte': lastOrderDateLte,
        'ordering': ordering,
        'page': page,
        'page_size': pageSize,
        'paid_amount_gte': paidAmountGte,
        'paid_amount_lte': paidAmountLte,
        'product': product,
        'registered': registered,
        'search': search,
      },
    });
  }

  /**
   * @param clientType Filtrează după tipul clientului: potential, active_with_account, active_without_account.
 *
 * * `potential` - Potential clients - without completed orders and without accounts
 * * `active_with_account` - Active clients with accounts - have completed orders and user accounts
 * * `active_without_account` - Active clients without accounts - have completed orders but no user accounts
   * @param email 
   * @param excludeIds comma separated list
   * @param ids comma separated list
   * @param lastOrderDateGte Filtrează clienții cu data ultimei comenzi mai mare sau egală decât valoarea transmisă.
   * @param lastOrderDateLte Filtrează clienții cu data ultimei comenzi mai mică sau egală decât valoarea transmisă.
   * @param ordering Ordering fields: id, created, modified, email, phone, first_name, last_name, last_order_date, paid_amount
   * @param paidAmountGte Filtrează clienții cu suma plătită mai mare sau egală decât valoarea transmisă.
   * @param paidAmountLte Filtrează clienții cu suma plătită mai mică sau egală decât valoarea transmisă.
   * @param product 
   * @param registered Filtrează clienții în funcție de existența unui cont de utilizator asociat.
   * @param search Search fields: email, first_name, last_name, phone
   * @returns binary 
   * @throws ApiError
   */
  public static usersCustomersXlsxRetrieve(
clientType?: 'active_with_account' | 'active_without_account' | 'potential',
email?: string,
excludeIds?: string,
ids?: string,
lastOrderDateGte?: string,
lastOrderDateLte?: string,
ordering?: string,
paidAmountGte?: number,
paidAmountLte?: number,
product?: Array<number>,
registered?: boolean,
search?: string,
): CancelablePromise<Blob> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/users/customers/xlsx/',
      query: {
        'client_type': clientType,
        'email': email,
        'exclude_ids': excludeIds,
        'ids': ids,
        'last_order_date_gte': lastOrderDateGte,
        'last_order_date_lte': lastOrderDateLte,
        'ordering': ordering,
        'paid_amount_gte': paidAmountGte,
        'paid_amount_lte': paidAmountLte,
        'product': product,
        'registered': registered,
        'search': search,
      },
    });
  }

  /**
   * @param broker 
   * @param email 
   * @param excludeIds comma separated list
   * @param excludeStatus Exclude utilizatorii care au oricare dintre statusurile transmise.
 *
 * * `active` - Activ
 * * `pending` - E-mail în așteptare
 * * `disabled` - Disabled
 * * `deleted` - Deleted
   * @param identificationNumber 
   * @param ids comma separated list
   * @param isBroker Filtrează utilizatorii în funcție de apartenența la un broker.
   * @param isStaff Filtrează utilizatorii în funcție de existența accesului de staff.
   * @param ordering Ordering fields: id, email, first_name, last_name
   * @param origin * `admin` - Admin
 * * `register` - Register
 * * `google` - Google
   * @param page A page number within the paginated result set.
   * @param pageSize Number of results to return per page.
   * @param permissions Filtrează utilizatorii după permisiunile echipei.
 *
 * * `section_orders` - Secțiunea Comenzi
 * * `section_customers` - Secțiunea Clienți
 * * `section_reports` - Secțiunea Rapoarte
 * * `section_registers` - Secțiunea registre
 * * `section_sales` - Secțiunea vânzări
 * * `section_financial` - Dashboard financiar
 * * `sales_panel` - Panou de vânzări
 * * `contracts_registry` - Borderoul contractelor
 * * `no_organization_limit` - Fără limită de organizații/brokeri
 * * `no_personal_limit` - Vizualizare contracte fără restricții
 * * `confirm_settlement` - Confirmare decontarea
 * * `confirm_refund` - Confirmare rambursarea
 * * `manual_ecec_creation` - Creare manuală E-CEC
 * * `manage_ecec_instances` - Manage ECEc Instances
 * * `confirm_bank_transfer` - Confirmarea transferului bancar
 * * `rca_documents` - Acces la documetele din RCA
 * * `financial_data` - Acces la date financiare
 * * `approve_order` - Aprobare comanda
 * * `change_contract` - Modificarea sau rezilierea contractului
 * * `fiscal_code_entity` - Gestionare organizații fără IDNO
 * * `rca_mark_as_deleted` - Anulare polițe RCA
 * * `online_orders` - Acces la comenzi online
 * * `inspection` - Acces pentru inspecție CASCO
 * * `re_apply_order` - Reaplicare comandă
 * * `section_settings` - Secțiunea Setări
 * * `section_external_api` - Secțiunea API Extern
 * * `vehicles_settings` - Configurarea setărilor vehiculelor
 * * `edit_insured_limit_config` - Edit own capital & max insured percent
 * * `section_despagubiri` - Secțiunea Despăgubiri
 * * `manage_reserve_types` - Gestionarea tipurilor de rezerve de daune
 * * `section_claim_case_rcai` - Secțiunea dosarelor de despăgubiri RCAI
 * * `claim_case_rcai_operator` - Operator dosare de despăgubiri RCAI
 * * `claim_case_rcai_supervisor` - Supervisor dosare de despăgubiri RCAI
 * * `section_claim_case_rcae` - Secțiunea dosarelor de despăgubiri RCAE
 * * `claim_case_rcae_operator` - Operator dosare de despăgubiri RCAE
 * * `claim_case_rcae_supervisor` - Supervisor dosare de despăgubiri RCAE
 * * `claim_anti_fraud` - Echipa antifraudă
 * * `claim_case_rcai_expert` - Expert în daune de asigurare RCAI
 * * `claim_case_confirm_payment` - Confirmarea plății despăgubirilor
 * * `director` - Director
 * * `product_rca` - RCA
 * * `product_green_card` - Carte Verde
 * * `product_medical_travel` - Asigurarea medicală
 * * `product_accidents` - Asigurarea de accidente
 * * `product_casco` - Casco
 * * `product_property` - Asigurarea bunurilor
 * * `product_cmr` - Asigurarea CMR
 * * `product_financial` - Asigurarea riscurilor financiare de călătorie
 * * `product_guarantees` - Asigurări de garanții
   * @param search Search fields: email, first_name, last_name, phone
   * @param ssoId 
   * @param status Filtrează utilizatorii după status.
 *
 * * `active` - Activ
 * * `pending` - E-mail în așteptare
 * * `disabled` - Disabled
 * * `deleted` - Deleted
   * @param teams 
   * @returns PaginatedUserDetailList 
   * @throws ApiError
   */
  public static usersUsersList(
broker?: number,
email?: string,
excludeIds?: string,
excludeStatus?: Array<'active' | 'deleted' | 'disabled' | 'pending'>,
identificationNumber?: string,
ids?: string,
isBroker?: boolean,
isStaff?: boolean,
ordering?: string,
origin?: 'admin' | 'google' | 'register' | null,
page?: number,
pageSize?: number,
permissions?: Array<'approve_order' | 'change_contract' | 'claim_anti_fraud' | 'claim_case_confirm_payment' | 'claim_case_rcae_operator' | 'claim_case_rcae_supervisor' | 'claim_case_rcai_expert' | 'claim_case_rcai_operator' | 'claim_case_rcai_supervisor' | 'confirm_bank_transfer' | 'confirm_refund' | 'confirm_settlement' | 'contracts_registry' | 'director' | 'edit_insured_limit_config' | 'financial_data' | 'fiscal_code_entity' | 'inspection' | 'manage_ecec_instances' | 'manage_reserve_types' | 'manual_ecec_creation' | 'no_organization_limit' | 'no_personal_limit' | 'online_orders' | 'product_accidents' | 'product_casco' | 'product_cmr' | 'product_financial' | 'product_green_card' | 'product_guarantees' | 'product_medical_travel' | 'product_property' | 'product_rca' | 'rca_documents' | 'rca_mark_as_deleted' | 're_apply_order' | 'sales_panel' | 'section_claim_case_rcae' | 'section_claim_case_rcai' | 'section_customers' | 'section_despagubiri' | 'section_external_api' | 'section_financial' | 'section_orders' | 'section_registers' | 'section_reports' | 'section_sales' | 'section_settings' | 'vehicles_settings'>,
search?: string,
ssoId?: string,
status?: Array<'active' | 'deleted' | 'disabled' | 'pending'>,
teams?: Array<number>,
): CancelablePromise<PaginatedUserDetailList> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/users/users/',
      query: {
        'broker': broker,
        'email': email,
        'exclude_ids': excludeIds,
        'exclude_status': excludeStatus,
        'identification_number': identificationNumber,
        'ids': ids,
        'is_broker': isBroker,
        'is_staff': isStaff,
        'ordering': ordering,
        'origin': origin,
        'page': page,
        'page_size': pageSize,
        'permissions': permissions,
        'search': search,
        'sso_id': ssoId,
        'status': status,
        'teams': teams,
      },
    });
  }

  /**
   * @param id A unique integer value identifying this utilizator.
   * @returns UserDetail 
   * @throws ApiError
   */
  public static usersUsersRetrieve(
id: number,
): CancelablePromise<UserDetail> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/users/users/{id}/',
      path: {
        'id': id,
      },
    });
  }

  /**
   * @returns UserProfile 
   * @throws ApiError
   */
  public static usersUsersProfileRetrieve(): CancelablePromise<UserProfile> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/users/users/profile/',
    });
  }

  /**
   * @param requestBody 
   * @returns UserProfile 
   * @throws ApiError
   */
  public static usersUsersProfilePartialUpdate(
requestBody?: PatchedUserProfile,
): CancelablePromise<UserProfile> {
    return __request(OpenAPI, {
      method: 'PATCH',
      url: '/users/users/profile/',
      body: requestBody,
      mediaType: 'application/json',
    });
  }

}
